import type { DatePickerProps as AntdDatePickerProps } from 'antd';
import clsx from 'clsx';
import { DatePicker as AntdDatePicker } from 'components/UI/antdDayjs';
import React from 'react';
import dayjs, { Dayjs } from 'utils/dayjs';

import s from './DatePicker.module.less';
import { usePublicHolidays } from './usePublicHolidays';

const format = ['DD.MM.YYYY', 'YYYY-MM-DD'];

// `AntdDatePicker` is generic over the value type (to support `multiple`), so
// `React.ComponentProps<typeof AntdDatePicker>` can't resolve it and falls back to
// `unknown`. Pin the generics explicitly via `DatePickerProps` instead.
type Props = AntdDatePickerProps<Dayjs, false> & {
  showWeekNumber?: boolean;
  highlightPublicHolidays?: boolean;
  hidePlaceholder?: boolean;
  width?: string;
};

/**
 * DatePicker component with week number
 * @param showWeekNumber - whether to show week number, default is true
 * @param highlightPublicHolidays - whether to show public holidays, default is true
 * @param hidePlaceholder - whether to show placeholders like "Select date"
 * or "Invalid date", default is false
 */
const DatePicker: React.FC<Props> = ({
  showWeekNumber = true,
  highlightPublicHolidays = true,
  hidePlaceholder = false,
  width,
  ...props
}) => {
  const { handlePanelChange, isPublicHoliday } = usePublicHolidays({
    value: dayjs(props.value).isValid() ? dayjs(props.value) : dayjs(),
    highlightPublicHolidays,
  });

  const popupClassName = clsx(props.popupClassName, {
    [s.popup]: showWeekNumber,
  });

  const className = clsx(s.root, props.className, {
    [s.datePicker]: showWeekNumber,
    [s.hidePlaceholder]: hidePlaceholder,
  });

  return (
    <AntdDatePicker
      format={format}
      style={{ width }}
      {...props}
      popupClassName={popupClassName}
      className={className}
      onPanelChange={handlePanelChange}
      dateRender={
        showWeekNumber
          ? (current) => {
              const style: React.CSSProperties = {};
              const isFirstWeekDay = current.weekday() === 0;
              const weekNumber = isFirstWeekDay ? current.isoWeek() : null;
              const weekday = current.format('dddd'); // Monday ... Sunday
              const isWeekend = weekday === 'Sunday' || weekday === 'Saturday';

              return (
                <div
                  className={clsx('ant-picker-cell-inner', {
                    [s.nonWorkingDay]: isWeekend || isPublicHoliday(current),
                  })}
                  style={style}
                >
                  {weekNumber && <div className={s.weekNumber}>{weekNumber}</div>}
                  {current.date()}
                </div>
              );
            }
          : props.dateRender
      }
    />
  );
};

export default DatePicker;
